import { useRef } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  clearCourseRouteGeometryUnlessManualPolyline,
  courseHasUnsavedStationChangeAtom,
  CourseMetadataKeys,
  coursePotentialPassengersAtom,
  dropdownStationListAtom,
  errorsCrudContentAtomFamily,
  IApiError,
  ICourseDto,
  ICourseStationInfo,
  inheritLegMetricsForManualCourseChange,
  Log,
  mergeInsertedCourseStations,
  normalizeCourseMetadata,
  normalizeErrors,
  RebuildRouteTypeEnum,
  SimpleResult,
  StationApi,
  useSystemParams,
  useTimezone,
  ZERO_TIME_HHmm,
} from "@ryde/shared";

import { useDropdownSourceContext } from "../../contexts";
import { useCourseDtoActions } from "./use-course-dto-actions";

export const useCourseStationActions = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { doNotAutoUpdateCourses } = useSystemParams();
  const { updateEditingCourseMetadata, applyEditingCourseMetadataLocally } =
    useCourseDtoActions();
  const setStationsDropdownItems = useSetRecoilState(dropdownStationListAtom);
  const setErrors = useSetRecoilState(errorsCrudContentAtomFamily("course"));
  const setCoursePotentialPassengers = useSetRecoilState(
    coursePotentialPassengersAtom,
  );
  const setCourseHasUnsavedStationChange = useSetRecoilState(
    courseHasUnsavedStationChangeAtom,
  );
  const { getDropdownItems } = useDropdownSourceContext();
  const potentialPassengersFetchSeqRef = useRef(0);

  const courseAddStationAction = useEvent(
    async (
      courseDto: ICourseDto,
      stationAddId: number | undefined,
    ): Promise<SimpleResult<void>> => {
      if (!stationAddId) {
        return new SimpleResult({
          success: false,
          error: t("errors.chooseStationToAdd"),
        });
      }

      const stationsInfoSnapshot = [
        ...(courseDto?.metadata?.stationsInfo || []),
      ];
      const prevStationsIds = stationsInfoSnapshot.map(x => x.id);
      const prevStationsIdsSet = new Set(prevStationsIds);

      const addedStations: ICourseStationInfo[] = [];

      if (!prevStationsIdsSet.has(stationAddId)) {
        const stationInfoFromRide =
          await StationApi.fetchStationInfo(stationAddId);

        if (!stationInfoFromRide?.length || !stationInfoFromRide) {
          return new SimpleResult({
            success: false,
            error: t("errors.invalidStationFetched", {
              stationId: stationAddId,
            }),
          });
        }

        const stationInfoFrom: ICourseStationInfo = stationInfoFromRide[0];
        const lastStationTime =
          stationsInfoSnapshot[stationsInfoSnapshot.length - 1]?.timeArrival ||
          ZERO_TIME_HHmm;
        // Manual ordering adds the station as the second row with "00:00" so the stable time-sort
        // keeps it there; the auto flow keeps the prior behaviour (inherit the last station's time).
        stationInfoFrom.timeArrival =
          doNotAutoUpdateCourses || !prevStationsIds.length
            ? ZERO_TIME_HHmm
            : lastStationTime;
        addedStations.push(stationInfoFrom);
      }

      const combinedStationsInfos = mergeInsertedCourseStations(
        stationsInfoSnapshot,
        addedStations,
      );

      const metadataToUpdate = { ...(courseDto?.metadata || {}) };

      metadataToUpdate.stationsInfo = combinedStationsInfos;
      const metadataRecounted = normalizeCourseMetadata({
        metadata: metadataToUpdate,
        reorderStationsByTime: !doNotAutoUpdateCourses,
        timezone,
      });

      const hasManualPolyline = Boolean(
        courseDto?.metadata?.[CourseMetadataKeys.isManualPolylineEdit],
      );

      // Manual mode / drawn polyline: keep leg metrics in sync without backend route recalc.
      const metadataToSave =
        doNotAutoUpdateCourses || hasManualPolyline
          ? inheritLegMetricsForManualCourseChange({
              previousStationsInfo: stationsInfoSnapshot,
              metadata: metadataRecounted,
            })
          : metadataRecounted;

      const metadataForUpdate = clearCourseRouteGeometryUnlessManualPolyline({
        metadata: metadataToSave,
        doNotAutoUpdateCourses,
      });

      Log.debug(
        "Composed metadata after add station for checking dry save ride:",
        metadataForUpdate,
      );

      applyEditingCourseMetadataLocally({
        metadata: metadataForUpdate,
        skipReorderStationsByTime: true,
      });
      setCourseHasUnsavedStationChange(true);

      const result = await updateEditingCourseMetadata({
        metadata: metadataForUpdate,
        optimize: false,
        skipApiCheck: hasManualPolyline,
        rebuildType: RebuildRouteTypeEnum.KeepOrder,
        skipReorderStationsByTime: true,
      });

      return result;
    },
  );

  const fetchStations = useEvent(async (): Promise<void> => {
    const stations = await getDropdownItems?.("station");
    setStationsDropdownItems(stations || []);
  });

  const fetchDropdownData = useEvent(async () => {
    await Promise.all([fetchStations()]);
  });

  const fetchPotentialStationsPassengers = useEvent(
    async (stationIds: number[]): Promise<void> => {
      const validStationIds =
        stationIds?.filter(
          (id): id is number => Number.isFinite(id) && id > 0,
        ) ?? [];

      if (!validStationIds.length) {
        return;
      }

      const fetchSeq = ++potentialPassengersFetchSeqRef.current;

      try {
        const stationInfoFromRide =
          await StationApi.fetchPotentialPassengersInfo(validStationIds);

        if (fetchSeq !== potentialPassengersFetchSeqRef.current) {
          return;
        }

        setCoursePotentialPassengers(stationInfoFromRide);
      } catch (e: unknown) {
        if (fetchSeq !== potentialPassengersFetchSeqRef.current) {
          return;
        }

        Log.errorAndToast(t("errors.failedToFetchPotentialPassengers"), e);
        setErrors(normalizeErrors(e as IApiError, t));
      }
    },
  );

  return {
    courseAddStationAction,
    fetchPotentialStationsPassengers,
    fetchDropdownData,
  };
};
